/**
 * Dynamic asset-field renderer driven by the active form template.
 *
 * The server's `GET /api/sites/:siteId/required-asset-fields` returns a
 * spec list (one entry per `is_required` `asset.*` mapping in the
 * jurisdiction's template). This component renders one labeled input
 * per spec — enum -> <select>, text -> <input>, boolean -> toggle —
 * and honors `showWhen` for conditionally-visible fields.
 *
 * Used by:
 *   - Field tech workflow (`DeviceQuickAddStep`) to replace the
 *     hard-coded `isMckinneySite` branches.
 *   - Office asset detail page to replace `HAZARD_GATED_CITIES`.
 *   - Office "Add Device" surfaces on sites + jobs detail.
 *
 * Adding a new jurisdiction-specific input never requires changing
 * this component: the registry entry on the server + an `is_required`
 * tick in the form-mapper UI is enough.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
    type AssetColumnInputType,
    type ShowWhenSpec,
    evaluateShowWhen,
    getAssetSpecByPath,
} from "@/lib/asset-path-registry";

/** API response shape from `/api/sites/:siteId/required-asset-fields`. */
export interface RequiredAssetColumnDto {
    path: string;
    column: string;
    bodyKey: string;
    label: string;
    inputType: AssetColumnInputType;
    enum: readonly string[] | null;
    enumLabels: Record<string, string> | null;
    showWhen: ShowWhenSpec | null;
}

interface RequiredAssetFieldsResponse {
    assetType: string;
    templateId: string | null;
    /** #1600 — the water provider's display name, for copy that names the city. */
    jurisdictionName: string | null;
    requiredColumns: RequiredAssetColumnDto[];
}

/**
 * React-Query wrapper for the required-fields endpoint. Exposed so
 * callers can pre-fetch / share state alongside their own queries.
 */
export function useRequiredAssetFields(siteId: string | null | undefined, assetType: string = "backflow") {
    return useQuery({
        queryKey: ["required-asset-fields", siteId, assetType],
        enabled: !!siteId,
        queryFn: async () => {
            const params = new URLSearchParams({ assetType });
            return api.get<RequiredAssetFieldsResponse>(`/api/sites/${siteId}/required-asset-fields?${params}`);
        },
        staleTime: 60_000,
    });
}

/**
 * Map of `bodyKey → current value` matching the controlled form
 * state callers maintain. `null` and `""` are treated as "empty" by
 * the missing-fields helper.
 */
export type DynamicAssetValues = Record<string, string | null | undefined>;

interface DynamicAssetFieldsProps {
    requiredColumns: RequiredAssetColumnDto[];
    values: DynamicAssetValues;
    onChange: (next: DynamicAssetValues) => void;
    /** Optional className for the wrapping <div>. */
    className?: string;
    /** Optional className applied to each Field row. */
    fieldClassName?: string;
}

/**
 * Build a `data_source_path → value` map for showWhen evaluation by
 * combining the controlled `values` (keyed by bodyKey) with the
 * registry's reverse mapping.
 */
function valuesByPathForShowWhen(
    requiredColumns: RequiredAssetColumnDto[],
    values: DynamicAssetValues,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const spec of requiredColumns) {
        out[spec.path] = values[spec.bodyKey] ?? null;
    }
    return out;
}

export function DynamicAssetFields({
    requiredColumns,
    values,
    onChange,
    className,
    fieldClassName,
}: DynamicAssetFieldsProps) {
    if (requiredColumns.length === 0) return null;

    const pathValues = valuesByPathForShowWhen(requiredColumns, values);

    return (
        <div className={className}>
            {requiredColumns.map((spec) => {
                if (!evaluateShowWhen(spec.showWhen ?? undefined, pathValues)) return null;
                const current = values[spec.bodyKey] ?? "";
                return (
                    <div key={spec.path} className={fieldClassName ?? "mb-3"}>
                        <label className="text-sm font-medium block mb-1.5">{spec.label}</label>
                        {spec.inputType === "enum" && spec.enum ? (
                            <Select
                                className="h-12"
                                value={typeof current === "string" ? current : ""}
                                onChange={(e) => onChange({ ...values, [spec.bodyKey]: e.target.value || null })}
                            >
                                <option value="">Select…</option>
                                {spec.enum.map((value) => (
                                    <option key={value} value={value}>
                                        {spec.enumLabels?.[value] ?? value}
                                    </option>
                                ))}
                            </Select>
                        ) : spec.inputType === "boolean" ? (
                            <input
                                type="checkbox"
                                checked={current === "true"}
                                onChange={(e) =>
                                    onChange({
                                        ...values,
                                        [spec.bodyKey]: e.target.checked ? "true" : "false",
                                    })
                                }
                            />
                        ) : (
                            <Input
                                className="h-12"
                                value={typeof current === "string" ? current : ""}
                                onChange={(e) => onChange({ ...values, [spec.bodyKey]: e.target.value })}
                                placeholder={`Enter ${spec.label.toLowerCase()}`}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Return the subset of `requiredColumns` whose corresponding values
 * are blank — used by save handlers to short-circuit the network
 * round-trip with a friendly inline error. Mirrors the server-side
 * gate evaluation (same `showWhen` rule, same blank check).
 */
export function findMissingDynamicValues(
    requiredColumns: RequiredAssetColumnDto[],
    values: DynamicAssetValues,
): RequiredAssetColumnDto[] {
    const pathValues = valuesByPathForShowWhen(requiredColumns, values);
    const out: RequiredAssetColumnDto[] = [];
    for (const spec of requiredColumns) {
        if (!evaluateShowWhen(spec.showWhen ?? undefined, pathValues)) continue;
        const v = values[spec.bodyKey];
        if (v === null || v === undefined || (typeof v === "string" && v.trim() === "")) {
            out.push(spec);
        }
    }
    return out;
}

/**
 * Seed initial dynamic values from an existing asset record. Walks
 * the registry's reverse map: for each `requiredColumn`, look up the
 * asset column and use that value (or empty string).
 */
export function seedDynamicValuesFromAsset(
    requiredColumns: RequiredAssetColumnDto[],
    asset: Record<string, unknown> | null | undefined,
): DynamicAssetValues {
    const out: DynamicAssetValues = {};
    if (!asset) return out;
    for (const spec of requiredColumns) {
        const registrySpec = getAssetSpecByPath(spec.path);
        const column = registrySpec?.column ?? spec.column;
        const raw = asset[column];
        out[spec.bodyKey] = raw == null ? "" : String(raw);
    }
    return out;
}
